import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyOverview } from './useSurveyOverview';
import { getTopicbaseRequest } from '../services/topicbase';

export interface ITopicItem {
  code: string;
  id: string;
  order: number;
  estimatedResponseTime: number;
  text: { [key: string]: any };
}

export interface ITopic {
  code: string;
  id: string;
  numberOfItems: number;
  order: number;
  title: { [key: string]: any };
  shortDescription: { [key: string]: any };
  items: ITopicItem[];
}

export interface IGenerateContentsArray {
  code: string;
  description: string;
  id: string;
  name: string;
  numberOfItems: number;
  numberOfTopics: number;
  order: number;
  title: { [key: string]: any };
  topics: ITopic[];
  expanded?: boolean;
}

export interface IStaticData {
  introText: string | null;
  outroText: string | null;
  helpText: string | null;
}

export const useSurveyContents = (surveyType: string, surveyId: string, orgName: string) => {
  const [topicsBase, setTopics] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [openQuestions, setOpenQuestions] = useState<{ code: string }[]>();
  const [staticData, setStaticData] = useState<IStaticData>({
    introText: null,
    outroText: null,
    helpText: null,
  });

  const [loading, setLoading] = useState(false);
  const [hellAndEndInitial, setHellAndEndInitial] = useState<any>({});
  const [linkToPreview, setLinkToPreview] = useState('');

  const {
    i18n: { language },
  } = useTranslation();
  const { getSurveyContents } = useSurveyOverview();

  const generateContentsArray = useMemo(
    () =>
      topicsBase?.length > 0
        ? topicsBase.map(base => {
          const selectedCategories = categories.map(({ code }) => code) ?? [];

          const topics = base?.topics.map((topic: any) => {
            const index = categories.findIndex(({ code }) => code === base?.code);
            const selectedTopics =
                categories[index]?.topics.map(({ code }: { code: any }) => code) ?? [];

            const items = topic?.items.map((item: any) => ({
              ...item,
              id: `${item?.code}+${item?.order}`,
              name: item?.text[language],
            }));

            return {
              ...topic,
              items,
              id: `${topic?.title[language]}+${topic?.code}`,
              name: topic?.title[language],
              shortDescription: topic?.shortDescription[language],
              selected: selectedTopics?.includes(topic?.code) ?? false,
            };
          });

          return {
            ...base,
            topics,
            id: `${base?.title[language]}+${base?.code}`,
            name: base?.title[language],
            description: base?.description[language],
            selected: selectedCategories.includes(base?.code) ?? false,
          };
        })
        : [],
    [categories, language, topicsBase]
  );

  const transformPlaceholders = (text: string) => {
    return text.replaceAll('[[Organization Name]]', orgName);
  };
  const textReplace = useMemo(() => {
    const { introText, outroText, helpText } = staticData;

    return {
      introText: introText && transformPlaceholders(introText),
      outroText: outroText && transformPlaceholders(outroText),
      helpText: helpText && transformPlaceholders(helpText),
    };
  }, [staticData]);

  useEffect(() => {
    (async () => {
      if (!surveyType) return;

      try {
        setLoading(true);

        const surveyCategoriesReq = await getSurveyContents(surveyId);

        const { introText, outroText, title, helpText, preview_link, openQuestions } =
          surveyCategoriesReq;

        setHellAndEndInitial({ introText, outroText, title, helpText });
        setStaticData({
          introText: introText && introText[language],
          outroText: outroText && outroText[language],
          helpText: helpText && helpText[language],
        });
        const surveyCategories = surveyCategoriesReq.scope;

        setCategories(surveyCategories);

        const topicbase = await getTopicbaseRequest(surveyType, language);

        const topicsBaseArr = topicbase?.categories || [];
        setOpenQuestions(openQuestions);
        setTopics(topicsBaseArr);
        setLinkToPreview(preview_link || '');
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    })();
  }, [language, surveyId, surveyType]);

  return {
    generateContentsArray,
    language,
    loading,
    staticData: textReplace,
    hellAndEndInitial,
    linkToPreview,
    openQuestions,
  };
};
